<template>
  <div class="container">
    <router-link :to="'/dashboard/bidders/' + auction.id" class="alt-link" >&lt; Back to Bidders!</router-link>

    <div class="align-center" v-if="loading">
      <InlineSpinner />
    </div>

    <div class="component-container" v-else>
      <h1 class="align-center">Closing Auction</h1>

      <div>
        <div class="status-indicators clearfix">
          <div class="status" :class="{ active: auction.status === auctionStatuses.PROCESSING.value }">
            <div><v-icon :color="auction.status === auctionStatuses.PROCESSING.value ? 'pfxBrand' : 'grey lighten-1'">lens</v-icon></div>
            Purchase Agreement
          </div>
          <div class="status" :class="{ active: auction.status === auctionStatuses.IN_ESCROW.value }">
            <div><v-icon :color="auction.status === auctionStatuses.IN_ESCROW.value ? 'pfxBrand' : 'grey lighten-1'">lens</v-icon></div>
            In Escrow
          </div>
          <div class="status" :class="{ active: (auction.status === auctionStatuses.SOLD.value || auction.status === auctionStatuses.CLOSED.value) }">
            <div><v-icon :color="(auction.status === auctionStatuses.SOLD.value || auction.status === auctionStatuses.CLOSED.value) ? 'pfxBrand' : 'grey lighten-1'">lens</v-icon></div>
            Closed
          </div>
        </div>

        <div class="grey-box-container box">
          <div class="clearfix">
            <div class="col13">
              <img :src="auctionPhoto(auction)" />
            </div>
            <div class="col23">
              <h2>{{ auction.address }}</h2>
              <div>Ended {{ auction.endDate | prettyDateTime(auction.metadata.timezone) }}</div>
              <div>Highest Bid {{ auction.currentPrice | prettyMoney }}</div>
              <div>Reserve Price {{ auction.reservePrice | prettyMoney }}</div>
              <div>{{ auction.participants.winner + auction.participants.buyer + auction.participants.abandon }} Bidders</div>
            </div>
          </div>
        </div>

        <div v-if="auction.status === auctionStatuses.PROCESSING.value">
          <p>
            <label for="choose">Choose the Winner</label><br/>
            <select id="choose" style="min-width:50%" v-model="selectedWinner" @change="selectedWinnerChanged">
              <option v-for="participant in participants" :key="participant.id" :value="participant.id">
                {{ participant.injected.profile.firstName }} {{ participant.injected.profile.lastName }}
                ({{ getMaxBidForParticipant(participant.userId) | prettyMoney }})
              </option>
            </select>
          </p>
        </div>

        <div v-if="loadingApplication">
          <InlineSpinner />
        </div>
        <template v-if="!loadingApplication">

          <div class="status-notification">
            <div class="color-bar">Auction Winner Details</div>
          </div>

          <div class="grey-box-container box">
            <h3>Personal Information</h3>
            <div class="clearfix row">
              <div class="col12">
                <h4>First Name</h4>
                <div>{{ application.injected.profile.firstName }}</div>
              </div>
              <div class="col12">
                <h4>Last Name</h4>
                <div>{{ application.injected.profile.lastName }}</div>
              </div>
            </div>
            <div class="clearfix row">
              <div class="col12">
                <h4>Username</h4>
                <div>{{ application.injected.profile.alias }}</div>
              </div>
              <div class="col12">
                <h4>Cell Phone Number</h4>
                <div>{{ application.injected.profile.phone }}</div>
              </div>
            </div>
            <div class="clearfix row">
              <div class="col12">
                <h4>Email Address</h4>
                <div>{{ application.injected.profile.email }}</div>
              </div>
              <div class="col12">
                <h4>Name of Entity</h4>
                <div>{{ application.injected.profile.buyerEntity || '&mdash;' }}</div>
              </div>
            </div>
          </div>

          <div class="grey-box-container box">
            <h3>Agency Relationship</h3>
            <div class="clearfix row">
              <h4>Relationship</h4>
              <div>{{ agencyRelationshipLabel(application.agencyRelationship) }}</div>
            </div>
            <!-- SELLING_AGENT -->
            <template v-if="application.agencyRelationship === agencyRelationships.SELLING_AGENT.value">
              <div class="clearfix row">
                <div class="col12">
                  <h4>Selling Agent</h4>
                  <div v-if="sellingAgentProfile.id">Accepted invitation</div>
                  <div v-else class="red-text">Invited, but pending sign up</div>
                </div>
                <div class="col12">
                  <h4>Name</h4>
                  <div>{{ sellingAgentProfile.firstName }} {{ sellingAgentProfile.lastName }}</div>
                </div>
              </div>
              <div class="clearfix row">
                <div class="col12">
                  <h4>Email Address</h4>
                  <div>{{ sellingAgentProfile.email || sellingAgentInvitedEmailAddress }}</div>
                </div>
                <div class="col12">
                  <h4>Cell Phone Number</h4>
                  <div>{{ sellingAgentProfile.phone }}</div>
                </div>
              </div>
              <div class="clearfix row">
                <div class="col12">
                  <h4>Agent ID</h4>
                  <div>{{ sellingAgentProfile.dreNumber }}</div>
                </div>
                <div class="col12">
                  <h4>Brokerage ID</h4>
                  <div>{{ sellingAgentProfile.xxxxx }}</div><!-- TODO needs to be added to profile form / special signup additions for selling agent invitations? -->
                </div>
              </div>
              <div class="clearfix row">
                <div class="col12">
                  <h4>Company</h4>
                  <div>{{ sellingAgentProfile.xxxxx }}</div><!-- TODO needs to be added to profile form / special signup additions for selling agent invitations? -->
                </div>
              </div>
            </template>
            <!-- LISTING_AGENT -->
            <template v-else-if="application.agencyRelationship === agencyRelationships.LISTING_AGENT.value">
              <div class="clearfix row">
                <div class="col12">
                  <h4>Agent Email Address</h4>
                  <div>{{ listingAgentProfile.email }}</div>
                </div>
              </div>
              <div class="clearfix row">
                <div class="col12">
                  <h4>First Name</h4>
                  <div>{{ listingAgentProfile.firstName }}</div>
                </div>
                <div class="col12">
                  <h4>Last Name</h4>
                  <div>{{ listingAgentProfile.lastName }}</div>
                </div>
              </div>
            </template>
            <!-- MYSELF -->
            <template v-else-if="application.agencyRelationship === agencyRelationships.MYSELF.value">
              <div class="clearfix row">
                <div class="col12">
                  <h4>Agent ID</h4>
                  <div>{{ application.injected.profile.dreNumber }}</div>
                </div>
              </div>
            </template>
            <!-- unknown relationship -->
            <template v-else>
              <div class="clearfix row red-text">
                Unknown or unsupported agency relationship, please contact PFX.
              </div>
            </template>

          </div>

          <div class="grey-box-container box">
            <h3>Buyer's documents</h3>

            <div class="indent clearfix list-bullets">
              <FileList :auctionId="auction.id" :files="application.applicationFiles" :mutable="false" />
            </div>
          </div>

          <template v-if="auction.status === auctionStatuses.PROCESSING.value">
            <div v-if="selectedWinner === currentWinner">
              <fieldset class="align-right">
                <p>Before proceeding, please confirm that the Purchase Agreement was signed by all parties.</p>
                <button @click="setToInEscrowDialog()"><v-icon dark>check</v-icon> Confirm and change status to In Escrow</button>
              </fieldset>
            </div>
            <div v-if="selectedWinner !== currentWinner">
              <fieldset class="align-left">
                <button class="btn-neutral" @click="changeWinnerDialog()"><v-icon dark>shuffle</v-icon> Set a new winner</button>
              </fieldset>
            </div>
          </template>
          <template v-else-if="auction.status === auctionStatuses.IN_ESCROW.value">
            <fieldset class="align-right">
              <p>Before proceeding, please confirm that the escrow process has been finished.</p>
              <button class="btn-neutral" @click="setToProcessingDialog()"><v-icon dark>undo</v-icon> Change status back to Processing</button>
              <button @click="setToClosedDialog()"><v-icon dark>check</v-icon> Confirm and change status to Closed</button>
            </fieldset>
          </template>

        </template>

      </div>

    </div>
  </div>
</template>

<script>
  import InlineSpinner from './elements/InlineSpinner.vue'
  import { auctionRoles, agencyRelationships, agencyRelationshipLabel, auctionInvitationRoles, auctionStatuses, auctionStatusLabel } from '../utils/PfxUtils.js'
  import FileList from './elements/FileList.vue'

  export default {
    name: 'AuctionClosingFlow',
    components: {
      FileList,
      InlineSpinner
    },
    data() {
      return {
        auctionRoles,
        agencyRelationships,
        auctionStatuses,
        loading: true,
        auctionId: parseInt(this.$route.params.auctionId),
        auction: {},
        listingAgentProfile: {},
        participants: [],
        selectedWinner: -1,
        currentWinner: -2,
        bids: [],

        loadingApplication: true,
        application: {},
        sellingAgentProfile: {},
        sellingAgentInvitedEmailAddress: ''
      }
    },
    created() {
      this.loadAuctionData()
    },
    methods: {
      agencyRelationshipLabel,

      loadAuctionData() {
        const that = this
        that.loading = true

        this.$PfxBackendApi.getAuction(that.auctionId).then(res => {
          console.log('getAuction res', res)
          that.auction = res.data || {}
          const subdomain = that.auction.tenant.subdomain
          that.loading = 2

          that.$PfxBackendApi.getBidsForAuction(subdomain, that.auctionId).then(res => {
            console.log('getBidsForAuction res', res)
            that.bids = res.data || []

            that.$PfxBackendApi.getAuctionParticipants(subdomain, that.auction.id).then(res => {
              console.log('getAuctionParticipants res', res)
              let data = res.data || []

              // filter only buyer/winner/abandon participants roles, and pick the currentWinner
              data = data.filter(participant => {
                if (participant.role === auctionRoles.WINNER.value) {
                  that.currentWinner = participant.id
                  that.selectedWinner = participant.id
                }
                return participant.role === auctionRoles.BUYER.value
                  || participant.role === auctionRoles.WINNER.value
                  || participant.role === auctionRoles.ABANDON.value
              })
              that.loading += data.length

              // sort by highest bid
              data.sort((a, b) => {
                const aa = this.getMaxBidForParticipant(a.userId)
                const bb = this.getMaxBidForParticipant(b.userId)
                return aa < bb ? 1 : aa === bb ? 0 : -1
              })

              // fetch buyer profiles
              data.forEach(participant => {
                participant.injected = {}
                participant.injected.profile = {}

                that.$PfxBackendApi.getUser(participant.userId).then(res => {
                  console.log('getUser (participant) res', res)
                  participant.injected.profile = res.data || {}
                }).catch(err => {
                  console.error('getUser (participant) err', err)
                  this.$notify({type: 'danger', message: 'Cannot fetch data buyer profile' })
                }).then(() => that.loading--)
              })
              that.participants = data
              that.loadAuctionRoleData()

            }).catch(err => {
              console.error('getAuctionParticipants err', err)
              this.$notify({type: 'danger', message: 'Cannot fetch data about auction participants' })
              that.loading = false

            }).then(() => that.loading--)

          }).catch(err => {
            console.error('getBidsForAuction res', err)
            this.$notify({type: 'danger', message: 'Cannot fetch data about bids' })
          }).then(() => that.loading--)


        }).catch(err => {
          console.error('getAuction err', err)
          this.$notify({type: 'danger', message: 'Cannot fetch data about auction' })
          that.loading = false
        })
      },

      selectedWinnerChanged() {
        if (this.auction.status !== auctionStatuses.PROCESSING.value) {
          this.$store.commit('openDialog', 'Auction status is already "' + auctionStatusLabel(this.auction.status) + '" and thus the winner cannot be changed.')
          this.selectedWinner = this.currentWinner
          return
        }
        console.log('selectedWinnerChanged [%s] currentWinner', this.selectedWinner, this.currentWinner)
        this.loadAuctionRoleData()
      },

      loadAuctionRoleData() {
        const that = this
        const auctionRoleId = that.selectedWinner
        const subdomain = that.auction.tenant.subdomain
        that.loadingApplication = 2

        that.application = that.participants.find(participant => {
          return participant.id === auctionRoleId
        })
        console.log('loadAuctionRoleData application set', that.application)

        // fetch selling agent
        if (that.application.agencyRelationship === agencyRelationships.SELLING_AGENT.value) {
          that.loadingApplication++
          that.$PfxBackendApi.getAuctionSellingAgent(subdomain, that.auction.id, auctionRoleId).then(res => {
            console.log('getAuctionSellingAgent res', res)
            const agentRole = (res.data && res.data.sellingAgentRole) || {}
            if (agentRole.role === auctionRoles.SELLING_AGENT.value && agentRole.userId) {
              that.loadingApplication++
              console.log('sellingAgentUserId [%s]', agentRole.userId)
              that.$PfxBackendApi.getUser(agentRole.userId).then(res => {
                console.log('getUser sellingAgentUserId res', res)
                that.sellingAgentProfile = res.data || {}
              }).catch(err => {
                console.log('getUser sellingAgentUserId err', err)
                this.$notify({type: 'danger', message: 'Cannot fetch selling agent profile'})
              }).then(() => that.loadingApplication--)
            }
          }).catch(err => {
            console.error('getAuctionSellingAgent err', err)
            this.$notify({type: 'danger', message: 'Cannot fetch data about selling agent'})
          }).then(() => that.loadingApplication--)
        }

        // fetch auction invitations for this auction (resolving selling agent who has not yet accepted invitation)
        that.$PfxBackendApi.getAuctionInvitations(subdomain, that.auction.id).then(res => {
          console.log('getAuctionInvitations res', res)
          const invitations = res.data || []
          const sellingAgentInvitation = invitations.reverse().find(invitation => {
            return invitation.auctionInvitationRole === auctionInvitationRoles.SELLING_AGENT.value &&
              invitation.auctionRole.id === auctionRoleId &&
              invitation.auctionRole.agencyRelationship === auctionRoles.SELLING_AGENT.value
          })
          console.log('getAuctionInvitations sellingAgentInvitation', sellingAgentInvitation)
          that.sellingAgentInvitedEmailAddress = sellingAgentInvitation && sellingAgentInvitation.email || null
        }).catch(err => {
          console.error('getAuctionInvitations err', err)
          this.$notify({type: 'danger', message: 'Cannot fetch data about selling agent invitation'})
        }).then(() => that.loadingApplication--)

        // fetch listing agent profile
        const listingAgentSub = that.auction.listingAgent
        that.$PfxBackendApi.getUser(listingAgentSub).then(res => {
          console.log('getUser (listing agent) res', res)
          that.listingAgentProfile = res.data || {}
        }).catch(err => {
          console.error('getUser (listing agent) res', err)
          this.$notify({type: 'danger', message: 'Cannot fetch listing agent profile'})
        }).then(() => that.loadingApplication--)
      },

      getMaxBidForParticipant(userId) {
        let max = 0
        this.bids
          .filter(bid => bid.userId === userId)
          .forEach(bid => max = Math.max(max, bid.amount))
        return max
      },

      auctionPhoto(auction) {
        if (auction && auction.metadata && auction.metadata.photos && Array.isArray(auction.metadata.photos) && auction.metadata.photos.length > 0) {
          return auction.metadata.photos[0]
        } else {
          return require('../assets/placeholder-photos.jpg')
        }
      },

      setToInEscrowDialog() {
        this.$store.commit('openDialog', {
          text: 'You are about to set the auction to "In Escrow".\n\n' +
          'Please confirm that the Purchase Agreement was signed by all parties.\n\n' +
          'Are you sure about proceeding?',
          buttons: [
            { title: 'Cancel', class: 'btn-neutral' },
            {
              title: 'Set status to In Escrow',
              handler: () => {
                this.$store.commit('closeDialog')
                this.setToInEscrow()
              }
            }
          ]
        })
      },

      setToInEscrow() {
        if (this.selectedWinner !== this.currentWinner) {
          return
        }

        const that = this
        that.$store.commit('setLoadingSpinner', true)
        that.$PfxBackendApi.changeAuctionStatus(this.auction.tenant.subdomain, this.auctionId, this.auctionStatuses.IN_ESCROW.value).then(res => {
          console.log('changeAuctionStatus res', res)
          that.loadAuctionData()
        }).catch(err => {
          console.error('changeAuctionStatus error', err)
          this.$notify({type: 'danger', message: 'Unexpected error during changing auction status'})
        }).then(() => that.$store.commit('setLoadingSpinner', false))
      },

      setToProcessingDialog() {
        this.$store.commit('openDialog', {
          text: 'You are about to set the auction back to "Processing" status.\n\n' +
          'This means that the current escrow process is canceled, and you can choose a different winner.\n\n' +
          'Are you sure about proceeding?',
          buttons: [
            { title: 'Cancel', class: 'btn-neutral' },
            {
              title: 'Set status to Processing',
              handler: () => {
                this.$store.commit('closeDialog')
                this.setToProcessing()
              }
            }
          ]
        })
      },

      setToProcessing() {
        if (this.selectedWinner !== this.currentWinner) {
          return
        }

        const that = this
        that.$store.commit('setLoadingSpinner', true)
        that.$PfxBackendApi.changeAuctionStatus(this.auction.tenant.subdomain, this.auctionId, this.auctionStatuses.PROCESSING.value).then(res => {
          console.log('changeAuctionStatus res', res)
          that.loadAuctionData()
        }).catch(err => {
          console.error('changeAuctionStatus error', err)
          this.$notify({type: 'danger', message: 'Unexpected error during changing auction status'})
        }).then(() => that.$store.commit('setLoadingSpinner', false))
      },

      setToClosedDialog() {
        this.$store.commit('openDialog', {
          text: 'You are about to set the auction to "Closed". This cannot be undone.\n\n' +
          'Please confirm that the escrow process is finished.\n\n' +
          'Are you sure about proceeding?',
          buttons: [
            { title: 'Cancel', class: 'btn-neutral' },
            {
              title: 'Set status to Closed',
              handler: () => {
                this.$store.commit('closeDialog')
                this.setToClosed()
              }
            }
          ]
        })
      },

      setToClosed() {
        const that = this
        that.$store.commit('setLoadingSpinner', true)
        that.$PfxBackendApi.changeAuctionStatus(this.auction.tenant.subdomain, this.auctionId, this.auctionStatuses.CLOSED.value).then(res => {
          console.log('changeAuctionStatus res', res)
          that.loadAuctionData()
        }).catch(err => {
          console.error('changeAuctionStatus error', err)
          this.$notify({type: 'danger', message: 'Unexpected error during changing auction status'})
        }).then(() => that.$store.commit('setLoadingSpinner', false))
      },

      changeWinnerDialog() {
        this.$store.commit('openDialog', {
          text: 'You are about to change the auction winner. Are you sure?',
          buttons: [
            { title: 'Cancel', class: 'btn-neutral' },
            {
              title: 'Change winner',
              handler: () => {
                this.$store.commit('closeDialog')
                this.changeWinner()
              }
            }
          ]
        })
      },

      changeWinner() {
        if (this.selectedWinner === this.currentWinner) {
          return
        }

        const that = this
        that.$store.commit('setLoadingSpinner', true)
        that.$PfxBackendApi.changeAuctionWinner(this.auction.tenant.subdomain, this.auctionId, this.selectedWinner).then(res => {
          console.log('changeAuctionWinner res', res)
          that.loadAuctionData()
        }).catch(err => {
          console.error('changeAuctionWinner error', err)
          this.$notify({type: 'danger', message: 'Unexpected error during changing auction status'})
        }).then(() => that.$store.commit('setLoadingSpinner', false))
      }

    }
  }
</script>

<style lang="scss" scoped>
  @import '../pfx.scss';

  .col12 { @include column(1/2, $cycle: 2); }
  .col13 { @include column(1/3); }
  .col23 { @include column(2/3); }
  .status { @include column(1/3, $gutter: 0); }
  img { width: 100%; }

  .component-container {
    @include center($max-width: 720px);
  }

  .status-indicators {
    padding: 1rem 0 2rem;
    text-align: center;
    color: #aaa;
    .active {
      color: #555;
      font-weight: 600;
    }
    .status {
      div {
        color: #aaa;
        background-image: linear-gradient(180deg,
          rgba(0,0,0,0) calc(50% - 1px), // TODO buggy on some browsers
          rgba(192,192,192,0.5) calc(50%),
          rgba(0,0,0,0) calc(50% + 1px));
      }
      &:first-child {
        div {
          background-size: 50% 100%;
          background-position: 100%;
        }
      }
      &:last-child {
        div {
          background-size: 50% 100%;
        }
      }
    }
  }

  .status-notification {
    .color-bar {
      text-align: center;
      background-color: $color-gold;
      background-image: $gradient-gold;
      color: #000;
      padding: 0.25em;
      font-size: 1.2rem;
    }
    .status-content {
      border: 1px solid #d8d8d8;
      border-top: 0;
      padding: 1rem;
    }
    margin-bottom: 1rem;
  }

  .box {
    margin-bottom: 1rem;

    h3 {
      font-size: 1.2rem;
      font-weight: 600;
      margin-top: 2rem;
      &:first-child { margin-top: 0; }
    }
    h4 {
      margin: 0 0 .25rem;
      font-weight: 600;
      color: #666;
    }
    .row {
      margin: 0 0 1.25rem;
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/AuctionClosingFlow.vue